import React, { useEffect, useRef, useState } from 'react'
import { type View, type ViewStyle } from 'react-native'

import styled from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Display } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/display/Display'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { BodyTextSize, DisplaySize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

const TextContainer = styled.View<{ fitContent?: boolean }>(({ fitContent }) => ({
  minWidth: 0,
  maxWidth: '100%',
  flexShrink: 1,
  ...(fitContent
    ? { flexGrow: 0, flexBasis: 'auto' }
    : {
        width: '100%',
        flexGrow: 1,
      }),
  overflow: 'hidden',
}))

const TruncatedBodyText = styled(BodyText)({
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  flexShrink: 1,
  lineHeight: 21,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const TruncatedDisplayText = styled(Display)({
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  flexShrink: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

type TruncatedTextProps = {
  accessibilityLabel?: string
  color?: string
  disableTooltip?: boolean
  fitContent?: boolean
  header?: boolean
  resizeKey: number
  testID: string
  text: string
  /** Overrides the tooltip copy and displays it even when the visible text fits. */
  tooltipContent?: string
}

export const TruncatedText: React.FC<TruncatedTextProps> = ({
  accessibilityLabel,
  color,
  disableTooltip,
  fitContent,
  header,
  resizeKey,
  testID,
  text,
  tooltipContent,
}) => {
  const containerRef = useRef<View | null>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    if (disableTooltip || tooltipContent !== undefined || typeof window === 'undefined') {
      setIsTruncated(false)
      return
    }

    const animationFrame = requestAnimationFrame(() => {
      const container = containerRef.current as unknown as HTMLElement | null
      const textElement = container?.firstElementChild as HTMLElement | null
      setIsTruncated(!!textElement && textElement.scrollWidth > textElement.clientWidth)
    })

    return () => cancelAnimationFrame(animationFrame)
  }, [disableTooltip, resizeKey, text, tooltipContent])

  const resolvedAccessibilityLabel = accessibilityLabel ?? tooltipContent ?? text

  const content = (
    <TextContainer ref={containerRef} fitContent={fitContent}>
      {header ? (
        <TruncatedDisplayText
          accessibilityLabel={resolvedAccessibilityLabel}
          noRole
          testID={tID(testID)}
          size={DisplaySize.EYEBROW}
          text={text}
          numberOfLines={1}
          ellipsizeMode='tail'
        />
      ) : (
        <TruncatedBodyText
          accessibilityLabel={resolvedAccessibilityLabel}
          testID={tID(testID)}
          text={text}
          color={color}
          size={BodyTextSize.SMALL}
          numberOfLines={1}
          ellipsizeMode='tail'
        />
      )}
    </TextContainer>
  )

  if (disableTooltip || (!isTruncated && tooltipContent === undefined)) return content

  const flexGrow = fitContent ? 0 : 1
  return (
    <Tooltip
      testID={`${testID}-truncatedText`}
      content={tooltipContent ?? text}
      accessibilityLabel={resolvedAccessibilityLabel}
      hoverEnabled
      openOnPress
      placement='top'
      wrapperStyle={
        {
          flexGrow,
          flexShrink: 1,
          maxWidth: '100%',
          minWidth: 0,
          display: 'flex',
        } as ViewStyle
      }
      style={
        {
          flexGrow,
          flexShrink: 1,
          maxWidth: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        } as ViewStyle
      }
    >
      {content}
    </Tooltip>
  )
}
